import React from 'react';
import productConfig from '../config/productConfig';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageMeta from '@/components/seo/PageMeta';

const TermsOfUse: React.FC = () => {
  const { brandPrefix, productType } = productConfig;
  const fullProductName = `${brandPrefix} ${productType}`;
  const supportEmailNew = 'support@ginylil.com';

  return (
    <div className="flex flex-col min-h-screen bg-gray-950">
      <PageMeta
        title="Terms Of Use And Service Rules | skore.info"
        description="Read the skore.info terms of use, including acceptable use, service description, data sources, contact details, and liability limits."
        canonicalPath="/terms"
        keywords={['terms of use', 'skore.info', 'sports app terms']}
      />
      <Header />
      
      <main className="container mx-auto px-4 py-8 flex-grow">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">Terms of Use</h1>
        
        <div className="max-w-4xl mx-auto bg-gray-900/80 p-8 rounded-xl border border-gray-700">
          <div className="prose prose-invert max-w-none">
            <p className="mb-4 text-gray-300">Last Updated: {new Date().toLocaleDateString()}</p>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Introduction</h2>
              <p className="text-gray-300">
                Welcome to {fullProductName}. By accessing or using our website and services, you agree to be bound by these Terms of Use.
                If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">User Data</h2>
              <p className="mb-3 text-gray-300">
                We only collect data related to the app to customize your experience. We may use cookies or session/local storage
                to save your selections. You are responsible for the security of your data.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Service Description</h2>
              <p className="mb-3 text-gray-300">
                {fullProductName} is a static web application designed to help sports fans revisit games without spoilers.
                It allows users to explore past games and decide what to watch based on how exciting the match was,
                without revealing the actual score or outcome.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Acceptable Use</h2>
              <p className="mb-3 text-gray-300">You agree not to use our services to:</p>
              <ul className="list-disc pl-6 mb-3 text-gray-300">
                <li>Violate any applicable law or regulation</li>
                <li>Infringe on the rights of others</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Engage in any activity that could harm or disrupt our services</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Intellectual Property</h2>
              <p className="text-gray-300">
                Our website, services, and all related content, features, and functionality are owned by Ginylil S.L. or its licensors
                and are protected by copyright, trademark, and other intellectual property laws.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Data Sources</h2>
              <p className="text-gray-300">
                Our service reads from a publicly hosted JSON file that is auto-updated every hour. We do not use official team logos
                due to legal restrictions; instead, we use custom team circles with abbreviations and colors.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Disclaimer of Warranties</h2>
              <p className="text-gray-300">
                Our services are provided "as is" and "as available" without any warranties of any kind, either express or implied.
                We do not guarantee that our services will be uninterrupted, secure, or error-free.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Limitation of Liability</h2>
              <p className="text-gray-300">
                In no event shall Ginylil S.L. be liable for any indirect, incidental, special, consequential, or punitive damages,
                arising out of or relating to your use of our services.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">External Links</h2>
              <p className="text-gray-300">
                Our service may contain links to external websites or resources that are not owned or controlled by us.
                We have no control over, and assume no responsibility for, the content, privacy policies, or practices
                of any third-party websites or resources. You acknowledge and agree that we shall not be responsible or
                liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection
                with the use of or reliance on any such content, goods, or services available on or through any such
                websites or resources.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Changes to Terms</h2>
              <p className="text-gray-300">
                We may update these Terms of Use from time to time. We will notify you of any changes by posting the new Terms on this page
                and updating the "Last Updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-white">Contact Us</h2>
              <p className="text-gray-300">
                If you have any questions about these Terms of Use, please contact us at {supportEmailNew}.
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TermsOfUse;
